import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Check, Minus, Plus, Sparkles, MessageCircle, Star, ArrowRight, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';
import { formatCents, cn } from '@/utils/cn';
import { MyShowLogoFull } from '@/components/Logo';
import type { Plan, BillingCycle } from '@/types/plan';
import { calcPlanTotalCents, calcPlanAnnualTotalCents, calcAnnualSavingsCents } from '@/types/plan';

const FEATURE_LABELS: Record<string, string> = {
  agent_integration: 'Agent ETL (ERP/balança)',
  realtime_price_sync: 'Atualização de preço em tempo real',
  multi_store: 'Multi-loja (filiais ilimitadas)',
  custom_branding: 'Branding customizado',
  api_access: 'API REST de integração',
  scheduling: 'Agendamento de playlists',
  html_slides: 'Slides HTML customizados',
  weather_slides: 'Slides de clima e data/hora',
  analytics_basic: 'Analytics básico',
  analytics_advanced: 'Analytics avançado',
  sla_99_5: 'SLA 99,5%',
  dedicated_account_manager: 'Gerente de conta dedicado',
  onboarding_self_service: 'Onboarding self-service + vídeo',
  onboarding_guided_1h: 'Onboarding guiado (1h remoto)',
  onboarding_guided_2h: 'Onboarding guiado (2h remoto)',
  onboarding_followup_30d: 'Acompanhamento 30 dias',
  support_email: 'Suporte por email',
  support_whatsapp_business_hours: 'Suporte WhatsApp (horário comercial)',
};

function planLimits(p: Plan): string[] {
  const out: string[] = [];
  out.push(p.max_products == null ? 'Produtos ilimitados' : `Até ${p.max_products.toLocaleString('pt-BR')} produtos`);
  out.push(`${(p.max_storage_mb / 1024).toLocaleString('pt-BR')} GB de mídia`);
  out.push(p.max_users == null ? 'Usuários ilimitados' : `${p.max_users} usuários`);
  if (p.max_stores != null) out.push(p.max_stores === 1 ? '1 loja' : `${p.max_stores} lojas`);
  return out;
}

// Variantes de reveal — mesmo estilo da LandingPage (Linear/Vercel).
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function Reveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      variants={{
        hidden: { opacity: 0, y: 18 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay } },
      }}
    >
      {children}
    </motion.div>
  );
}

export function PricingPage() {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [tvs, setTvs] = useState(3);
  const [calcPlanCode, setCalcPlanCode] = useState<string>('pro');

  const { data: plans = [], isLoading } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => apiClient.getPlans().then(r => r.data as Plan[]),
  });

  const calcPlan = useMemo(
    () => plans.find(p => p.code === calcPlanCode) ?? plans[0],
    [plans, calcPlanCode],
  );

  const calcMonthlyEquiv = calcPlan ? calcPlanTotalCents(calcPlan, tvs, cycle) : 0;
  const calcAnnualFull = calcPlan ? calcPlanAnnualTotalCents(calcPlan, tvs) : 0;
  const calcSavings = calcPlan ? calcAnnualSavingsCents(calcPlan, tvs) : 0;
  const calcFirstMonth = cycle === 'monthly' ? calcMonthlyEquiv : null;
  const calcPromo50 = Math.round(calcMonthlyEquiv * 0.5);

  return (
    <div className="min-h-screen bg-background antialiased selection:bg-brand-200/60">
      {/* Banner promo de lançamento */}
      <div className="gradient-brand text-white text-center py-2.5 px-4 text-sm font-medium relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(600px 80px at 50% 0%, rgba(255,255,255,.35), transparent)' }} />
        <span className="relative">
          <Sparkles className="inline h-4 w-4 mr-1 -mt-0.5" />
          <strong>PROMO DE LANÇAMENTO:</strong> 50% OFF nos 3 primeiros meses — só pros 50 primeiros clientes.
          Use o cupom <code className="bg-white/20 px-1.5 py-0.5 rounded">LANCAMENTO50</code> no checkout.
        </span>
      </div>

      {/* Header */}
      <header className="border-b border-border/60 bg-background/70 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" className="flex items-center group">
            <MyShowLogoFull height={36} className="transition-transform group-hover:scale-[1.03]" />
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            <Link to="/" className="hidden md:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Início</Link>
            <Link to="/login" className="inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Entrar</Link>
            <Button asChild size="sm" variant="gradient" className="btn-gradient-glow ml-2">
              <Link to="/cadastro">Criar minha TV grátis</Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero da página de preços */}
      <section className="relative overflow-hidden">
        <div className="orb bg-brand-400/30 w-[460px] h-[460px] -top-32 -left-32" />
        <div className="orb bg-fuchsia-400/25 w-[420px] h-[420px] top-20 -right-32" />
        <div aria-hidden className="absolute inset-0 bg-dots mask-fade opacity-70" />

        <div className="container-app relative py-16 md:py-24 text-center">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-3xl mx-auto">
            <motion.span variants={fadeUp} className="inline-block text-xs uppercase tracking-[0.25em] text-brand-700 font-semibold mb-4">
              Planos & Preços
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-display text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Preços{' '}
              <span className="relative inline-block">
                <span className="text-gradient-brand">honestos</span>
                <svg aria-hidden viewBox="0 0 220 14" className="absolute -bottom-2 left-0 w-full h-3 text-brand-500/70" preserveAspectRatio="none">
                  <path d="M2 9 C 60 1, 160 1, 218 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              .
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-7 text-lg md:text-xl text-muted-foreground leading-relaxed">
              A 1ª TV cobre a estrutura do plano (suporte, integrações, recursos).
              <br className="hidden md:block" />
              Cada TV adicional custa <strong className="text-foreground">R$ 49,90/mês</strong> — em qualquer plano.
            </motion.p>

            {/* Toggle mensal/anual */}
            <motion.div variants={fadeUp} className="mt-9 inline-flex rounded-full border border-border/70 bg-background/70 backdrop-blur p-1 shadow-soft">
              <button
                onClick={() => setCycle('monthly')}
                className={cn(
                  'px-5 py-2 text-sm rounded-full transition-colors',
                  cycle === 'monthly' ? 'bg-foreground text-background font-semibold shadow-sm' : 'text-muted-foreground hover:text-foreground'
                )}
              >Mensal</button>
              <button
                onClick={() => setCycle('annual')}
                className={cn(
                  'px-5 py-2 text-sm rounded-full transition-colors inline-flex items-center gap-1.5',
                  cycle === 'annual' ? 'bg-foreground text-background font-semibold shadow-sm' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Anual
                <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                  cycle === 'annual' ? 'bg-emerald-400/90 text-emerald-950' : 'bg-emerald-100 text-emerald-700'
                )}>-20%</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Cards de planos */}
      <section className="container-app pb-16 relative">
        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">Carregando planos…</div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
            {plans.map((p, idx) => {
              const isPro = p.code === 'pro';
              const isEnt = p.code === 'enterprise';
              const firstCents = cycle === 'monthly' ? p.first_tv_monthly_cents : Math.round(p.first_tv_annual_cents / 12);
              const addCents = cycle === 'monthly' ? p.price_per_tv_monthly_cents : Math.round(p.price_per_tv_annual_cents / 12);
              const featureKeys = Object.entries(p.features || {}).filter(([, v]) => v === true).map(([k]) => k);

              return (
                <Reveal key={p.id} delay={idx * 0.08}>
                  <div
                    className={cn(
                      'relative h-full rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1',
                      isPro
                        ? 'border-2 border-brand-600/70 bg-background shadow-glow'
                        : isEnt
                          ? 'bg-foreground text-background shadow-elev overflow-hidden'
                          : 'border border-border/70 bg-background/80 backdrop-blur shadow-soft hover:shadow-elev',
                    )}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 gradient-brand text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase shadow-md inline-flex items-center gap-1">
                        <Star className="h-3 w-3" /> Mais escolhido
                      </div>
                    )}
                    {isEnt && (
                      <div aria-hidden className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-500/30 blur-3xl" />
                    )}

                    <div className="relative">
                      <div className={cn('text-xs uppercase tracking-[0.18em] font-semibold mb-3',
                        isPro ? 'text-brand-700' : isEnt ? 'text-background/60' : 'text-muted-foreground'
                      )}>
                        {p.name}
                      </div>
                      <p className={cn('text-sm leading-relaxed mb-6',
                        isEnt ? 'text-background/70' : 'text-muted-foreground'
                      )}>
                        {p.description}
                      </p>

                      <div className="mb-6">
                        <div className="flex items-baseline gap-1">
                          <span className="font-display text-5xl font-extrabold tracking-tight">{formatCents(firstCents)}</span>
                          <span className={cn(isEnt ? 'text-background/60' : 'text-muted-foreground')}>/mês</span>
                        </div>
                        <div className={cn('text-xs mt-1', isEnt ? 'text-background/60' : 'text-muted-foreground')}>
                          a 1ª TV {cycle === 'annual' ? '(equiv. mensal · cobrado anual)' : ''}
                        </div>
                        <div className={cn('text-sm mt-3', isEnt ? 'text-background/80' : 'text-foreground')}>
                          + <strong>{formatCents(addCents)}</strong>/mês por TV adicional
                        </div>
                        {cycle === 'annual' && (
                          <div className={cn('text-xs mt-2', isEnt ? 'text-emerald-300' : 'text-emerald-600')}>
                            💰 Cobrança anual: {formatCents(p.first_tv_annual_cents)} (1ª TV)
                          </div>
                        )}
                      </div>

                      <Button
                        asChild
                        className={cn('w-full h-11', isPro && 'btn-gradient-glow shadow-glow')}
                        variant={isPro ? 'gradient' : isEnt ? 'secondary' : 'outline'}
                      >
                        <Link to={p.code === 'enterprise' ? '/cadastro?plan=enterprise&contact=1' : `/cadastro?plan=${p.code}`}>
                          {p.code === 'enterprise' ? 'Falar com vendas' : 'Quero vender mais'}
                        </Link>
                      </Button>

                      <ul className="mt-7 space-y-2.5 text-sm">
                        {planLimits(p).map((line, i) => (
                          <li key={i} className="flex gap-2">
                            <Check className={cn('h-4 w-4 shrink-0 mt-0.5', isEnt ? 'text-emerald-400' : 'text-emerald-500')} />
                            <span>{line}</span>
                          </li>
                        ))}
                        {featureKeys.map((k) => {
                          const label = FEATURE_LABELS[k];
                          if (!label) return null;
                          const highlight = k === 'agent_integration' || k === 'realtime_price_sync' || k === 'multi_store' || k === 'dedicated_account_manager';
                          return (
                            <li key={k} className={cn('flex gap-2', highlight && 'font-semibold')}>
                              {highlight ? (
                                <Sparkles className={cn('h-4 w-4 shrink-0 mt-0.5', isEnt ? 'text-orange-300' : 'text-brand-600')} />
                              ) : (
                                <Check className={cn('h-4 w-4 shrink-0 mt-0.5', isEnt ? 'text-emerald-400' : 'text-emerald-500')} />
                              )}
                              <span>{label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>

      {/* Calculadora */}
      {calcPlan && (
        <section className="container-app pb-24 relative">
          <Reveal>
            <div className="relative max-w-5xl mx-auto rounded-3xl border border-border/70 bg-gradient-to-br from-brand-50/70 via-background to-fuchsia-50/40 p-8 md:p-12 overflow-hidden shadow-elev">
              <div aria-hidden className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-300/40 blur-3xl" />
              <div aria-hidden className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-fuchsia-300/30 blur-3xl" />

              <div className="text-center mb-10 relative">
                <span className="inline-block text-xs uppercase tracking-[0.25em] text-brand-700 font-semibold mb-3">Calculadora</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Calcule o seu investimento.</h2>
                <p className="text-muted-foreground mt-3">Sem pegadinha. O valor que você vê aqui é o que você paga.</p>
              </div>

              <div className="grid md:grid-cols-3 gap-4 relative">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold mb-2 text-muted-foreground">Plano</label>
                  <select
                    value={calcPlanCode}
                    onChange={(e) => setCalcPlanCode(e.target.value)}
                    className="w-full bg-background/90 backdrop-blur border-2 border-border/70 rounded-xl px-4 py-3 font-semibold focus:border-brand-600 focus:outline-none transition-colors"
                  >
                    {plans.map(p => (
                      <option key={p.code} value={p.code}>
                        {p.name} — {formatCents(p.first_tv_monthly_cents)}/mês
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold mb-2 text-muted-foreground">Quantas TVs?</label>
                  <div className="flex items-center gap-2 bg-background/90 backdrop-blur border-2 border-border/70 rounded-xl px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => setTvs(t => Math.max(1, t - 1))}
                      className="h-9 w-9 rounded-lg hover:bg-muted/60 flex items-center justify-center transition-colors"
                      aria-label="Diminuir TVs"
                    ><Minus className="h-4 w-4" /></button>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={tvs}
                      onChange={(e) => setTvs(Math.max(1, Math.min(999, parseInt(e.target.value) || 1)))}
                      className="flex-1 text-center font-display font-bold text-2xl bg-transparent border-0 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setTvs(t => Math.min(999, t + 1))}
                      className="h-9 w-9 rounded-lg hover:bg-muted/60 flex items-center justify-center transition-colors"
                      aria-label="Aumentar TVs"
                    ><Plus className="h-4 w-4" /></button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold mb-2 text-muted-foreground">Ciclo</label>
                  <select
                    value={cycle}
                    onChange={(e) => setCycle(e.target.value as BillingCycle)}
                    className="w-full bg-background/90 backdrop-blur border-2 border-border/70 rounded-xl px-4 py-3 font-semibold focus:border-brand-600 focus:outline-none transition-colors"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="annual">Anual (-20%)</option>
                  </select>
                </div>
              </div>

              {/* Resultado */}
              <div className="mt-8 bg-background/95 backdrop-blur rounded-2xl p-6 md:p-8 grid md:grid-cols-2 gap-6 items-center shadow-soft border border-border/40">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Você paga</div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-display text-5xl md:text-6xl font-extrabold tracking-tight text-gradient-brand">
                      {formatCents(calcMonthlyEquiv)}
                    </span>
                    <span className="text-muted-foreground">
                      {cycle === 'annual' ? '/mês equiv.' : '/mês'}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatCents(cycle === 'monthly' ? calcPlan.first_tv_monthly_cents : Math.round(calcPlan.first_tv_annual_cents / 12))} (1ª TV)
                    {tvs > 1 && (
                      <> + {tvs - 1}× {formatCents(cycle === 'monthly' ? calcPlan.price_per_tv_monthly_cents : Math.round(calcPlan.price_per_tv_annual_cents / 12))} (TVs adicionais)</>
                    )}
                  </div>
                  {cycle === 'annual' && (
                    <div className="text-sm text-emerald-600 mt-2 font-semibold">
                      Cobrança anual de {formatCents(calcAnnualFull)} · economia de {formatCents(calcSavings)}/ano
                    </div>
                  )}
                </div>

                {/* Promo lançamento */}
                <div className="relative bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-5 overflow-hidden">
                  <div aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-orange-300/40 blur-2xl" />
                  <div className="relative">
                    <div className="text-xs uppercase tracking-wider font-bold text-orange-700 mb-1 flex items-center gap-1">
                      <Sparkles className="h-3.5 w-3.5" /> Promo de lançamento
                    </div>
                    <div className="font-display text-3xl font-extrabold text-orange-600 mb-1 tracking-tight">
                      {formatCents(calcPromo50)}<span className="text-sm font-normal">/mês</span>
                    </div>
                    <div className="text-xs text-orange-700">
                      nos 3 primeiros meses · cupom <code className="bg-background px-1.5 py-0.5 rounded font-mono">LANCAMENTO50</code>
                    </div>
                    {calcFirstMonth != null && (
                      <div className="text-xs text-orange-600 mt-2 font-semibold">
                        Economiza {formatCents((calcFirstMonth - calcPromo50) * 3)} nos 3 primeiros meses
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-7 text-center relative">
                <Button asChild size="lg" variant="gradient" className="btn-gradient-glow h-12 px-7 shadow-glow">
                  <Link to={`/cadastro?plan=${calcPlan.code}&tvs=${tvs}&cycle=${cycle}`}>
                    ⚡ Configurar minha loja grátis <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <div className="text-xs text-muted-foreground mt-3">
                  Sem cartão · Sem fidelidade · Cancele quando quiser.
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* FAQ */}
      <section className="relative py-20 overflow-hidden">
        <div aria-hidden className="absolute inset-0 bg-muted/20" />
        <div className="container-app relative">
          <Reveal className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-brand-700 font-semibold mb-3">FAQ</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Dúvidas sobre o preço.</h2>
          </Reveal>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              {
                q: 'Por que a 1ª TV é mais cara que as demais?',
                a: <>A 1ª TV cobre a estrutura completa do seu plano: suporte, integrações, recursos, onboarding.
                  A partir da segunda, você paga só o custo marginal de cada tela — <strong>R$ 49,90/mês</strong>.
                  Quanto mais TVs, mais barata fica a média.</>,
              },
              {
                q: 'E se eu tiver 5.000 produtos cadastrados?',
                a: <>Cabe perfeitamente no plano Pro (limite de 5.000). Se você tem mais que isso ou quer expandir, o Enterprise é ilimitado.</>,
              },
              {
                q: 'Tenho 3 lojas. Qual plano escolher?',
                a: <>O <strong>Enterprise</strong> foi feito pra isso — painel centralizado, sincronização independente por loja, gerente de conta dedicado.</>,
              },
              {
                q: 'Como funciona o anual com 20% off?',
                a: <>Pagando 12 meses à vista, você ganha o equivalente a 2,4 meses grátis. O desconto vale em TODOS os componentes da fatura — 1ª TV e TVs adicionais.</>,
              },
              {
                q: 'Tem fidelidade ou multa?',
                a: <>Não. Cancele quando quiser. Mensalidades pagas não são reembolsadas mas você não paga nada extra.</>,
              },
              {
                q: 'Suporte é igual em todos os planos?',
                a: <><strong>Sim.</strong> Email + WhatsApp em horário comercial em todos os planos. Qualidade não negocia — todo cliente é cliente.
                  No Enterprise você ainda tem gerente de conta dedicado e SLA 99,5%.</>,
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <details className="group border border-border/70 bg-background/80 backdrop-blur rounded-xl px-5 py-4 transition-all hover:shadow-soft open:shadow-soft">
                  <summary className="font-semibold cursor-pointer flex justify-between items-center list-none tracking-tight">
                    <span>{item.q}</span>
                    <span className="text-muted-foreground group-open:rotate-180 transition-transform ml-3">▾</span>
                  </summary>
                  <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{item.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="container-app py-10">
        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-2xl border border-border/60 bg-background/60 backdrop-blur p-5 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold">14 dias grátis</div>
                <div className="text-xs text-muted-foreground">Sem cartão, sem fidelidade</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold">Promo de lançamento</div>
                <div className="text-xs text-muted-foreground">50% off nos 3 primeiros meses</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold">Suporte WhatsApp</div>
                <div className="text-xs text-muted-foreground">Em todos os planos</div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA final */}
      <section className="container-app py-20">
        <Reveal>
          <div className="relative rounded-3xl gradient-brand p-12 md:p-16 text-white text-center overflow-hidden shadow-glow">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'radial-gradient(800px 200px at 20% 0%, rgba(255,255,255,.35), transparent), radial-gradient(600px 200px at 80% 100%, rgba(255,255,255,.25), transparent)',
            }} />
            <div aria-hidden className="absolute inset-0 bg-dots opacity-15" />

            <h2 className="relative font-display text-3xl md:text-5xl font-bold tracking-tight">
              Pronto pra começar?
            </h2>
            <p className="relative mt-4 text-white/90 max-w-xl mx-auto text-lg">
              14 dias grátis. Sem cartão. Setup em menos de 15 minutos.
            </p>
            <div className="relative mt-9 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" variant="secondary" className="h-12 px-7 bg-white text-brand-700 hover:bg-white/95 shadow-elev">
                <Link to="/cadastro">🚀 Criar minha primeira TV agora <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 bg-transparent border-white/70 text-white hover:bg-white hover:text-brand-700">
                <a href="https://wa.me/554699826101" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 py-8 bg-muted/20">
        <div className="container-app text-sm text-muted-foreground flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-4 flex-wrap">
            <span>© {new Date().getFullYear()} MyShow</span>
            <a
              href="https://visualsoftware.inf.br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs hover:text-foreground transition-colors"
            >
              <span>Powered by</span>
              <img
                src="https://visualsoftware.inf.br/assets/logo_visual_software-BBverSN6.webp"
                alt="Visual Software"
                className="h-6 w-auto"
                loading="lazy"
              />
            </a>
          </div>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Planos</Link>
            <Link to="/login" className="hover:text-foreground transition-colors">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
